import React from 'react'
import { Text } from '../ui'
import type { SupportCenterFormKind } from '../../types/supportCenterSpecialties'

/** Roteiros e orientações para o atendente (campos comuns e específicos). */

export function TriageAttendantHint({
  title,
  children,
}: {
  title?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-primary-200/70 bg-primary-50/60 dark:border-primary-800/50 dark:bg-primary-900/20 px-3 py-2.5 space-y-2">
      {title ? (
        <Text
          size="xs"
          className="font-semibold uppercase tracking-wide text-primary-800 dark:text-primary-300"
        >
          {title}
        </Text>
      ) : null}
      {children}
    </div>
  )
}

export function TriageQuote({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm italic text-gray-700 dark:text-gray-300 border-l-2 border-primary-400 dark:border-primary-500 pl-3">
      {children}
    </p>
  )
}

export function TriageNote({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" className="text-gray-700 dark:text-gray-300">
      {children}
    </Text>
  )
}

function cleanAttendantName(attendantName: string): string {
  return attendantName.replace(/\*+/g, '').trim() || '[seu nome]'
}

function teamLabel(
  formKind: SupportCenterFormKind | null | undefined,
  specializationName?: string | null
): string {
  if (formKind === 'psicologico') return 'do Núcleo de Psicólogas aqui da Central do PASI'
  if (formKind === 'nutricional') return 'da equipe de Nutrição aqui da Central do PASI'
  if (formKind === 'fitness') return 'da equipe de Fitness aqui da Central do PASI'
  if (formKind === 'juridico') return 'da equipe Jurídica aqui da Central do PASI'
  if (formKind === 'social') {
    return 'da equipe de Assistência Social aqui da Central do PASI'
  }

  const spec = specializationName?.replace(/\*+/g, '').trim()
  if (spec) return `da equipe de ${spec} aqui da Central do PASI`
  return 'da equipe da Central do PASI'
}

/** Abertura — campos comuns a todos os formulários */
export function openingScript(
  attendantName: string,
  formKind?: SupportCenterFormKind | null,
  specializationName?: string | null
): string[] {
  const name = cleanAttendantName(attendantName)
  return [
    `Oi, tudo bem? Meu nome é ${name} e eu faço parte ${teamLabel(formKind, specializationName)}. Eu estou entrando em contato para entender um pouquinho melhor o que está acontecendo com você e como nós podemos te ajudar.`,
    'Primeiro eu vou confirmar algumas informações e depois vou te fazer algumas perguntas para entender como você está e o que você está precisando neste momento.',
  ]
}

/** Motivo principal — comum a todos os formulários */
export const MAIN_REASON_OPENING =
  'Para eu conseguir entender um pouco melhor como te ajudar, você consegue me contar o que tem sido mais difícil para você neste momento?'

export const MAIN_REASON_IF_DIFFICULTY = [
  'Tudo bem, você não precisa me contar detalhes se não se sentir à vontade. Eu posso falar algumas situações que costumam levar as pessoas a procurar nosso atendimento e você me diz se alguma delas se parece com o que você está vivendo. Pode ser?',
  'Algumas pessoas procuram a gente porque estão muito preocupadas ou ansiosas; outras porque estão mais tristes, estressadas ou irritadas. Também pode ser por alguma dificuldade na família, no relacionamento, no trabalho ou nos estudos; por uma perda ou luto; por medos, inseguranças ou problemas para dormir. Alguma dessas situações tem a ver com o que você está vivendo?',
  'Qual delas mais se aproxima do que você está sentindo?',
  'Tem mais alguma coisa que também está acontecendo?',
]

export const MAIN_REASON_IF_REFUSES =
  'Tudo bem. Você não precisa falar sobre isso agora se não se sentir confortável.'

/** Avaliação de risco — formulário psicológico */
export const RISK_ROLE_NOTE =
  'O papel nessa etapa é IDENTIFICAR, não INVESTIGAR. Se aparecer ideação suicida atual, autoagressão recente, intenção de agredir alguém, violência, abuso, negligência ou qualquer situação que gere dúvida sobre a segurança do paciente: acolha, não demonstre susto, não faça julgamento e acione a psicóloga responsável conforme o fluxo do PASI.'

export const RISK_OPENING =
  'Agora eu preciso fazer algumas perguntas que fazemos para todas as pessoas que passam por esse primeiro atendimento. Algumas podem parecer mais pessoais, mas são importantes para sabermos se você está seguro e se precisa de algum cuidado mais rápido. Tudo bem?'

export const RISK_QUESTIONS = {
  suicidal_ideation:
    'Em algum momento, você já pensou que não queria mais viver ou pensou em tirar a própria vida?',
  self_harm:
    'Alguma vez você já se machucou de propósito, por exemplo, se cortando, se batendo ou fazendo alguma coisa para se ferir?',
  aggression_risk:
    'Em algum momento você ficou com tanta raiva ou tão nervoso que pensou em machucar alguém?',
  domestic_violence:
    'Tem alguma situação dentro da sua casa ou da sua família que faz você se sentir em perigo?',
  domestic_violence_clarify:
    'Tem alguém te machucando, ameaçando, te tratando de uma forma que te dá medo ou fazendo alguma coisa com você que você não quer?',
} as const

/** Fechamento — comum a todos os formulários */
export const CLOSING_SCRIPT =
  'Obrigada por conversar comigo e por compartilhar essas informações. Elas são importantes para que a nossa equipe possa entender melhor o que você está passando e como podemos te ajudar.'
